import React from 'react';

/**
 * Vertical membership-lifecycle diagram — an exact transcription of the mockup's
 * animated SVG (source: mockup L404–467), including the SMIL animations.
 * Kept as a standalone component so its markup can stay a verbatim copy.
 *
 * Fonts are inline styles, not SVG presentation attributes: Chakra's global
 * reset (`* { font: inherit }`) is author CSS, which outranks presentation
 * attributes and would silently replace Bebas/Roboto with the body font.
 */
const LIFECYCLE_FONTS = {
  stage: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px' },
  stageDark: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px' },
  sub: { fontFamily: 'Roboto, sans-serif', fontSize: '12.5px' },
  subDark: { fontFamily: 'Roboto, sans-serif', fontSize: '12px' },
  pill: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '12.5px' },
} satisfies Record<string, React.CSSProperties>;

const LifecycleDiagram = (): React.JSX.Element => (
  <div className="oq-lifecycle">
    <div className="oq-lifecycle__glow" aria-hidden="true" />
    <svg
      viewBox="0 0 410 620"
      role="img"
      aria-label="Membership lifecycle: New, Active, Expiring, Expired, and Deleted"
      className="oq-lifecycle__svg"
    >
      <defs>
        <clipPath id="vcards">
          <rect x="70" y="24" width="252" height="76" rx="14" />
          <rect x="70" y="138" width="252" height="76" rx="14" />
          <rect x="70" y="252" width="252" height="76" rx="14" />
          <rect x="70" y="366" width="252" height="76" rx="14" />
        </clipPath>
        <filter id="vsh" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="5" stdDeviation="7" floodColor="#0a0a0f" floodOpacity="0.10" />
        </filter>
        <marker id="vdown" markerWidth="12" markerHeight="12" refX="4" refY="8" orient="auto" markerUnits="userSpaceOnUse">
          <path d="M0,0 L4,8 L8,0 Z" fill="#c2ccd3" />
        </marker>
        <marker id="vgreen" markerWidth="12" markerHeight="12" refX="8" refY="4" orient="auto" markerUnits="userSpaceOnUse">
          <path d="M8,0 L0,4 L8,8 Z" fill="#16A34A" />
        </marker>
        <marker id="vred" markerWidth="12" markerHeight="12" refX="4" refY="8" orient="auto" markerUnits="userSpaceOnUse">
          <path d="M0,0 L4,8 L8,0 Z" fill="#DC2626" />
        </marker>
      </defs>

      {/* connectors */}
      <g stroke="#d2dae0" strokeWidth="3" fill="none" strokeLinecap="round">
        <line x1="40" y1="70" x2="40" y2="168" />
        <line x1="40" y1="184" x2="40" y2="282" />
        <line x1="40" y1="298" x2="40" y2="396" />
      </g>
      <path d="M40,412 V521" stroke="#DC2626" strokeWidth="3" fill="none" strokeLinecap="round" strokeDasharray="2 9" />

      {/* renew loop */}
      <path d="M322,290 H360 Q372,290 372,278 V188 Q372,176 360,176 H322" stroke="#16A34A" strokeWidth="2.5" fill="none" />
      <rect x="340" y="221" width="64" height="24" rx="12" fill="#ffffff" stroke="rgba(22,163,74,0.55)" />
      <text
        x="372"
        y="237"
        textAnchor="middle"
        style={LIFECYCLE_FONTS.pill}
        letterSpacing="1"
        fill="#16A34A"
      >
        RENEWS
      </text>

      {/* flowing pulses — hidden for prefers-reduced-motion via .oq-smil-pulse (tokens.css) */}
      <circle r="3.5" fill="#4AA3CC" className="oq-smil-pulse">
        <animateMotion dur="3s" repeatCount="indefinite" path="M40,62 V404" />
        <animate attributeName="opacity" values="0;1;1;0" dur="3s" repeatCount="indefinite" />
      </circle>
      <circle r="3.5" fill="#4AA3CC" className="oq-smil-pulse">
        <animateMotion dur="3s" begin="1s" repeatCount="indefinite" path="M40,62 V404" />
        <animate attributeName="opacity" values="0;1;1;0" dur="3s" begin="1s" repeatCount="indefinite" />
      </circle>
      <circle r="3.5" fill="#4AA3CC" className="oq-smil-pulse">
        <animateMotion dur="3s" begin="2s" repeatCount="indefinite" path="M40,62 V404" />
        <animate attributeName="opacity" values="0;1;1;0" dur="3s" begin="2s" repeatCount="indefinite" />
      </circle>

      {/* cards */}
      <rect x="70" y="24" width="252" height="76" rx="14" fill="#ffffff" stroke="rgba(10,10,15,0.06)" filter="url(#vsh)" />
      <rect x="70" y="138" width="252" height="76" rx="14" fill="#ffffff" stroke="rgba(10,10,15,0.06)" filter="url(#vsh)" />
      <rect x="70" y="252" width="252" height="76" rx="14" fill="#ffffff" stroke="rgba(10,10,15,0.06)" filter="url(#vsh)" />
      <rect x="70" y="366" width="252" height="76" rx="14" fill="#ffffff" stroke="rgba(10,10,15,0.06)" filter="url(#vsh)" />
      <rect x="70" y="490" width="252" height="76" rx="14" fill="#0A0A0F" filter="url(#vsh)" />
      <g clipPath="url(#vcards)">
        <rect x="70" y="24" width="6" height="76" fill="#2D8CBA" />
        <rect x="70" y="138" width="6" height="76" fill="#16A34A" />
        <rect x="70" y="252" width="6" height="76" fill="#F9A825" />
        <rect x="70" y="366" width="6" height="76" fill="#DC2626" />
      </g>

      {/* active pulse — hidden for prefers-reduced-motion via .oq-smil-pulse (tokens.css) */}
      <circle
        cx="40"
        cy="176"
        r="8"
        fill="none"
        stroke="#16A34A"
        strokeWidth="2"
        className="oq-smil-pulse"
      >
        <animate attributeName="r" values="8;20" dur="1.9s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.55;0" dur="1.9s" repeatCount="indefinite" />
      </circle>

      {/* dots */}
      <circle cx="40" cy="62" r="7.5" fill="#2D8CBA" stroke="#ffffff" strokeWidth="3" />
      <circle cx="40" cy="176" r="7.5" fill="#16A34A" stroke="#ffffff" strokeWidth="3" />
      <circle cx="40" cy="290" r="7.5" fill="#F9A825" stroke="#ffffff" strokeWidth="3" />
      <circle cx="40" cy="404" r="7.5" fill="#DC2626" stroke="#ffffff" strokeWidth="3" />
      <circle cx="40" cy="528" r="7.5" fill="#0A0A0F" stroke="#ffffff" strokeWidth="3" />

      {/* labels */}
      <text x="92" y="59" style={LIFECYCLE_FONTS.stage} letterSpacing="0.5" fill="#0A0A0F">
        NEW
      </text>
      <text x="92" y="81" style={LIFECYCLE_FONTS.sub} fill="#6B6B76">
        Signed up · on trial
      </text>
      <text x="92" y="173" style={LIFECYCLE_FONTS.stage} letterSpacing="0.5" fill="#0A0A0F">
        ACTIVE
      </text>
      <text x="92" y="195" style={LIFECYCLE_FONTS.sub} fill="#6B6B76">
        Fully paid-up member
      </text>
      <text x="92" y="287" style={LIFECYCLE_FONTS.stage} letterSpacing="0.5" fill="#0A0A0F">
        EXPIRING
      </text>
      <text x="92" y="309" style={LIFECYCLE_FONTS.sub} fill="#6B6B76">
        Renewal due soon
      </text>
      <text x="92" y="401" style={LIFECYCLE_FONTS.stage} letterSpacing="0.5" fill="#0A0A0F">
        EXPIRED
      </text>
      <text x="92" y="423" style={LIFECYCLE_FONTS.sub} fill="#6B6B76">
        Membership lapsed
      </text>
      <text x="92" y="525" style={LIFECYCLE_FONTS.stageDark} letterSpacing="0.5" fill="#ffffff">
        DELETED
      </text>
      <text x="92" y="546" style={LIFECYCLE_FONTS.subDark} fill="#c4c8d2">
        Removed 21 days after expiry
      </text>
    </svg>
  </div>
);

export default LifecycleDiagram;
